import { useMemo } from "react";

// Search filtering, sorting and grouping logic for the customer combobox.
export function useCustomerSearch(customerList, search, recentIds) {
  // Recently used customers (only shown when no search active)
  const recentCustomers = useMemo(() => {
    if (search.trim()) return [];
    return recentIds
      .map((id) => customerList.find((c) => c.id === id))
      .filter(Boolean);
  }, [recentIds, customerList, search]);

  // Filter and sort customers based on search
  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = !term
      ? customerList
      : customerList.filter((c) => {
          const haystack = [
            c.company_name,
            c.city,
            c.postal_code,
            c.category
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        });

    const categoryOrder = { Kunde: 0, Interessent: 1, Lieferant: 2 };
    return [...filtered].sort((a, b) => {
      const aOrder = categoryOrder[a.category] ?? 99;
      const bOrder = categoryOrder[b.category] ?? 99;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.company_name || "").localeCompare(b.company_name || "", "de");
    });
  }, [customerList, search]);

  // Group filtered customers by first letter
  const groupedCustomers = useMemo(() => {
    const groups = new Map();
    filteredCustomers.forEach((c) => {
      const firstChar = (c.company_name || "?").charAt(0).toUpperCase();
      const letter = /[A-Z]/.test(firstChar) ? firstChar : "#";
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter).push(c);
    });
    return Array.from(groups.entries());
  }, [filteredCustomers]);

  return { recentCustomers, filteredCustomers, groupedCustomers };
}
